import { colors } from '@/constants/colors'
import { type ReactNode, createContext, useContext } from 'react'

/**
 * What a card is painted, and every colour on it that follows from that.
 *
 * Two grounds, because the app's cards are drawn on both. The visit card is drawn twice: white
 * on Home, where its rows sit on the card itself under a mounted photograph, and blush on the
 * visit's own page, where they are a block inside a white card. The whitening page draws two side
 * by side — a white kit card and a blush refill card, so the two are told apart before either is
 * read.
 *
 * Everything in a row is dark ink on a light ground either way. What cannot be shared is
 * anything that has to be *a shade off* the card: a disc, an inner panel. Each of those is one
 * question — "what colour is this, on this ground" — and this file is the only place any of them
 * is answered, so a card cannot quietly disagree with the card beside it. A second lookup table
 * anywhere defeats the point of the context.
 *
 * It lived in `src/features/visits/` while it was the visit card's alone. `features/` is meant to
 * be pure logic and this is a React context, so it moved here once a second feature needed it.
 */
export type CardGround = 'white' | 'blush'

const GroundContext = createContext<CardGround>('blush')

export const CardGroundProvider = ({
	ground,
	children,
}: { ground: CardGround; children: ReactNode }) => (
	<GroundContext.Provider value={ground}>{children}</GroundContext.Provider>
)

export const useCardGround = () => useContext(GroundContext)

/**
 * The card's own fill: the ground, as a colour.
 *
 * So a card that is told what it stands on does not also have to be told what to paint itself,
 * which is how the two come apart.
 */
export const useGroundColor = () =>
	useCardGround() === 'white' ? colors.white : colors.blush

/**
 * The disc behind a row's mark: white on blush, blush on white.
 *
 * It is the same disc either way — a circle of the other ground — so the mark always stands in
 * something rather than floating on the card. A white disc on a white card is nothing, which is
 * what made this worth a context rather than a constant.
 */
export const useDiscColor = () =>
	useCardGround() === 'white' ? colors.blush : colors.white

/**
 * A quiet panel inside a card: the whitening slot's status row.
 *
 * Not the disc's colour, though on blush the two agree. On a white card a panel takes `surface`,
 * the near-white a white surface uses for exactly this — blush there would read as a second
 * object rather than as a recess. On a blush card it goes white, because that same grey on pink
 * is a third neutral for no reason.
 */
export const usePanelColor = () =>
	useCardGround() === 'white' ? colors.surface : colors.white
